import { useState, useEffect, useCallback } from 'react';
import type { BLEConnectionInfo, ConnectionState } from '../types/ble';
import { bleManager, StateChangeEvent } from '../lib/ble-manager';

export function useBLE() {
  const [info, setInfo] = useState<BLEConnectionInfo>(bleManager.connectionInfo);

  useEffect(() => {
    const handler = (e: Event) => {
      setInfo((e as StateChangeEvent).info);
    };

    bleManager.addEventListener('statechange', handler);
    // Sync initial state
    setInfo(bleManager.connectionInfo);

    return () => bleManager.removeEventListener('statechange', handler);
  }, []);

  const connect = useCallback(() => bleManager.connect(), []);
  const disconnect = useCallback(() => bleManager.disconnect(), []);

  return {
    ...info,
    connect,
    disconnect,
    isSupported: bleManager.isSupported,
    isConnected: info.state === 'connected',
    isReconnecting: info.state === 'reconnecting',
  };
}

/** Simple state-only hook for components that just need the connection badge */
export function useConnectionState(): ConnectionState {
  const [state, setState] = useState<ConnectionState>(bleManager.state);

  useEffect(() => {
    const handler = (e: Event) => {
      setState((e as StateChangeEvent).info.state);
    };
    bleManager.addEventListener('statechange', handler);
    return () => bleManager.removeEventListener('statechange', handler);
  }, []);

  return state;
}
